/**
 * shell.tsx — the shared app frame (§7.7, §7.8, §7.18): the icon rail, the
 * two-row title bar, and the status bar. Built ONCE here so the terminal
 * (this phase) and the site (U07) render the same shell. Presentational
 * only — the app owns state, data and handlers.
 */
import type { ReactNode } from 'react';

const cx = (...p: Array<string | false | undefined>) => p.filter(Boolean).join(' ');

/* ──────────────────────────────────────────────────────────── RAIL ── */
export interface RailItem { id: string; label: string; icon: ReactNode; badge?: number; }
export interface RailGroup { id: string; label: string; items: RailItem[]; }

/** §7.7 — icons only; active = filled pill; the group label fades in above
 * the group on hover; badge = a number; every item gets a right-side
 * tooltip (`data-tip` + `data-tip-pos="right"`), never a native `title`. */
export function Rail({ groups, current, onSelect, logo, orientation = 'vertical' }: {
  groups: RailGroup[]; current: string; onSelect: (id: string) => void; logo?: ReactNode;
  /** 'horizontal' — a bottom tab bar for phones (§7.7 mobile): row layout,
   * full width, group labels hidden (no room, and no hover on touch). */
  orientation?: 'vertical' | 'horizontal';
}) {
  return (
    <nav className="qt-rail" data-orientation={orientation === 'horizontal' ? 'horizontal' : undefined} aria-label="Sections">
      {logo && orientation === 'vertical' && <div className="qt-rail__logo">{logo}</div>}
      {groups.map((g) => (
        <div key={g.id} className="qt-rail__group">
          <span className="qt-rail__glabel" aria-hidden>{g.label}</span>
          {g.items.map((it) => (
            <button
              key={it.id}
              type="button"
              className="qt-rail__btn"
              aria-current={current === it.id}
              aria-label={it.label}
              data-tip={it.label}
              data-tip-pos="right"
              onClick={() => onSelect(it.id)}
            >
              {it.icon}
              {it.badge != null && it.badge > 0 && <span className="qt-rail__badge">{it.badge > 99 ? '99+' : it.badge}</span>}
            </button>
          ))}
        </div>
      ))}
    </nav>
  );
}

/* ────────────────────────────────────────────────────── TITLE ROW ── */
/** §7.8 row 1 — the drag region. left / center / right slots; anything
 * interactive inside marks itself no-drag via `.qt-titlerow`'s own CSS rule
 * (button/input/a/[data-nodrag]), so callers don't need to think about it. */
export function TitleRow({ left, center, right, drag = true }: {
  left?: ReactNode; center?: ReactNode; right?: ReactNode; drag?: boolean;
}) {
  return (
    <div className="qt-titlerow" data-tauri-drag-region={drag || undefined}>
      {left}
      <div className="qt-titlerow__center" data-tauri-drag-region={drag || undefined}>{center}</div>
      {right}
    </div>
  );
}

/** §7.8 row 2 — the trading strip: mode pill, day PnL, balance, risk. */
export function TradeRow({ children }: { children: ReactNode }) {
  return <div className="qt-traderow">{children}</div>;
}

export function ModePill({ mode, onClick }: { mode: 'paper' | 'real'; onClick?: () => void }) {
  return (
    <button type="button" className="qt-mode" data-mode={mode} onClick={onClick}>
      <span className="qt-badge__dot" aria-hidden />
      {mode === 'real' ? 'REAL' : 'PAPER'}
    </button>
  );
}

export function TickerButton({ symbol, price, changePct, onClick }: {
  symbol: string; price?: string; changePct?: number; onClick: () => void;
}) {
  const dir = changePct == null ? undefined : changePct > 0 ? 'up' : changePct < 0 ? 'dn' : undefined;
  return (
    <button type="button" className="qt-ticker" onClick={onClick}>
      <span>{symbol}</span>
      {price && <span className="qt-ticker__px">{price}</span>}
      {changePct != null && (
        <span className="num" style={{ color: dir === 'up' ? 'var(--bull)' : dir === 'dn' ? 'var(--bear)' : 'var(--mid)' }}>
          {changePct > 0 ? '+' : ''}{changePct.toFixed(2)}%
        </span>
      )}
    </button>
  );
}

/* ────────────────────────────────────────────────────── STATUS BAR ── */
export function StatusBar({ children }: { children: ReactNode }) {
  return <div className="qt-statusbar">{children}</div>;
}

export function StatusItem({ state, label, ms }: { state: 'ok' | 'slow' | 'down'; label: string; ms?: number }) {
  return (
    <span className={cx('qt-statusitem')}>
      <span className="qt-conn" data-state={state === 'ok' ? undefined : state} aria-hidden />
      {label}{ms != null && ` ${Math.round(ms)}ms`}
    </span>
  );
}

/* ───────────────────────────────────────────────────── TRANSITION ── */
/** §7.18: keyed on `id` so a section switch retriggers the animation —
 * 200ms translateX(12px) + fade in, off under prefers-reduced-motion. */
export function SectionTransition({ id, children }: { id: string; children: ReactNode }) {
  return <div key={id} className="qt-section">{children}</div>;
}
